import { ApiError } from './query.js';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

export interface ApiOptions extends RequestInit {
  body?: BodyInit | null;
  json?: unknown;
  searchParams?: Record<string, string | number | undefined>;
}

export async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const { json, searchParams, headers, ...rest } = options;
  const url = new URL(
    path.startsWith('http') ? path : `${API_BASE}${path}`,
    window.location.origin,
  );
  if (searchParams) {
    for (const [k, v] of Object.entries(searchParams)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }

  const finalHeaders: HeadersInit = {
    ...(json !== undefined ? { 'content-type': 'application/json' } : {}),
    ...headers,
  };
  const finalBody: BodyInit | null | undefined =
    json !== undefined ? JSON.stringify(json) : (rest.body as BodyInit | null | undefined);

  const res = await fetch(url.toString(), {
    credentials: 'include',
    ...rest,
    headers: finalHeaders,
    body: finalBody,
  });

  if (!res.ok) {
    let parsed: unknown = undefined;
    try {
      parsed = await res.json();
    } catch {
      /* non-JSON body */
    }
    let message = `api_${res.status}`;
    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
      const errStr = String((parsed as { error: unknown }).error);
      if (errStr) message = errStr;
    }
    throw new ApiError(message, res.status, parsed);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
